import { AsyncPipe, DecimalPipe, PercentPipe } from '@angular/common';
import { ChangeDetectionStrategy, Component, inject, input, OnInit, viewChild } from '@angular/core';
import { UntypedFormBuilder, UntypedFormGroup } from '@angular/forms';
import { MatDrawer, MatDrawerContainer } from '@angular/material/sidenav';
import { ProjectAssetMappingService } from '@app/cem/project/content/project-asset-mapping/project-asset-mapping.service';
import { AssetMappingConfiguration, IModuleAllocation } from '@app/cem/project/models/asset-mapping-configuration';
import { calculateSplitPercentages } from '@app/shared/utilities/math-utilities';
import { ButtonDirective } from '@progress/kendo-angular-buttons';
import { DataStateChangeEvent, GridComponent, GridDataResult, GridModule, RowArgs } from '@progress/kendo-angular-grid';
import { PagerInfoComponent, PagerNextButtonsComponent, PagerPrevButtonsComponent } from '@progress/kendo-angular-pager';
import { FilterDescriptor, State } from '@progress/kendo-data-query';
import { CostCenterAvailableService } from 'app/cem/project/services/cost-center-available.service';
import { ProjectAssetMappingCostCenterService } from 'app/cem/project/services/project-asset-mapping-cost-center.service';
import { CostCenter, CostCenterType, Project, ProjectAssetMapping } from 'app/shared/models';
import { BehaviorSubject, Observable } from 'rxjs';

@Component({
  selector: 'app-project-asset-mapping-cost-center',
  templateUrl: './project-asset-mapping-cost-center.component.html',
  styleUrls: ['./project-asset-mapping-cost-center.component.scss'],
  imports: [
    AsyncPipe,
    ButtonDirective,
    DecimalPipe,
    GridModule,
    MatDrawer,
    MatDrawerContainer,
    PagerInfoComponent,
    PagerNextButtonsComponent,
    PagerPrevButtonsComponent,
    PercentPipe,
  ],
  changeDetection: ChangeDetectionStrategy.Eager,
  providers: [
    CostCenterAvailableService,
  ],
})
export class ProjectAssetMappingCostCenterComponent implements OnInit {
  public costCenterAvailableService = inject(CostCenterAvailableService);
  public formBuilder = inject(UntypedFormBuilder);

  private selectionFilter = `&$filter=IsActive eq true and not contains(ProjectIds, '`;
  private projectFilter = '&$filter=CostCenterId ne null and ProjectId eq';
  private projectMappingSelect = '&$select=ProjectAssetMappingId,ProjectId,CostCenterId,PercentAllocation';
  private projectMappingExpand = '&$expand=CostCenter($select=CostCenterId,CostCenterNumber,Description;$expand=CostCenterType($select=Description))';
  private totalAllocation = new BehaviorSubject<number>(0);
  private hasPendingChange = false;
  private initialLoad = true;
  public selectedCostCenters: CostCenter[] = [];
  public isNew: boolean = false;
  public selectionInProgress: boolean = false;
  public showToggleAllocation: boolean;
  public totalCostCenters: number;
  public totalAllocationPct: number;
  public costCentersView: Observable<GridDataResult>;
  public costCenterMap: {};
  public availableCostCentersView: Observable<GridDataResult>;
  public costCenterGridState: State = { skip: 0, take: 500 };
  public availableCostCentersGridState: State = { skip: 0, take: 10 };
  public projectAssetMappingFormGroup: UntypedFormGroup;
  public selectionFormGroup: UntypedFormGroup;
  public allocationAllowed: boolean = false;
  public moduleIsEditable: boolean = false;

  readonly projectId = input<number>(undefined);
  readonly assetMappingConfig = input<AssetMappingConfiguration>(undefined);
  readonly projectAssetMappingCostCenterService = input<ProjectAssetMappingCostCenterService>(undefined);
  readonly projectAssetMappingService = input<ProjectAssetMappingService>(undefined);

  private readonly selectionGrid = viewChild<GridComponent>('selectionGrid');
  public project = input<Project>(undefined);

  constructor() {
    this.createProjectAssetMappingFormGroup = this.createProjectAssetMappingFormGroup.bind(this);
    this.createSelectionFormGroup = this.createSelectionFormGroup.bind(this);
  }

  public ngOnInit() {
    this.setValuesFromConfig();
    this.loadAvailableCostCenters();
    this.loadMappedCostCenters();
    this.setValuesFromCostCenters();
    this.subscribeTotalAllocation();
    this.subscribeToProjectModuleEvents();
  }

  private formatQueryString(): string {
    return `${this.projectFilter} ${this.projectId()} ${this.projectMappingSelect}${this.projectMappingExpand}`;
  }

  private formatSelectionQueryString(): string {
    return `${this.selectionFilter}${this.projectId()}')`;
  }

  private loadMappedCostCenters() {
    this.costCentersView = this.projectAssetMappingCostCenterService();
    this.projectAssetMappingCostCenterService().read(this.costCenterGridState, this.formatQueryString());
  }

  private loadAvailableCostCenters() {
    this.availableCostCentersView = this.costCenterAvailableService;
    this.costCenterAvailableService.read(this.availableCostCentersGridState, this.formatSelectionQueryString());
  }

  private convertStateForContainerIdContainsFilter(): State {
    let tempGridState: State = JSON.parse(JSON.stringify(this.availableCostCentersGridState));
    let hasContainsFilter = false;
    if (this.availableCostCentersGridState.filter) {
      tempGridState.filter.filters.map(x => {
        let currentFilter = x as FilterDescriptor;
        if (currentFilter.field === 'CostCenterNumber') {
          currentFilter.field = 'cast(CostCenterNumber,%27Edm%2EString%27)';
          currentFilter.operator = 'contains';
          x = currentFilter;
          hasContainsFilter = true;
        }
      });
    }

    if (hasContainsFilter)
      return tempGridState;
    else
      return null;
  }

  private subscribeTotalAllocation() {
    this.totalAllocation.subscribe(t => {
      let allocationPercentage = this.totalAllocation.getValue();
      this.totalAllocationPct = allocationPercentage;
      this.setToggleModule(this.totalCostCenters > 0 ? true : false);
      this.projectAssetMappingService().setModuleAllocation(
        {
          assetMappingConfigurationId: this.assetMappingConfig().AssetMappingConfigurationId,
          moduleId: this.assetMappingConfig().ModuleId,
          allocationPercentage: allocationPercentage || 0,
          hasAssets: this.totalCostCenters > 0 ? true : false,
          isActiveToggle: this.totalCostCenters > 0 ? true : false,
          isToggle: this.assetMappingConfig().ModuleProperties.IsToggleModule,
          hasPendingChange: this.hasPendingChange
        }
      );
    });

    this.projectAssetMappingService().getModulesToSaveChanges()
      .subscribe((event) => {
        event.map((data) => {
          const assetMappingConfig = this.assetMappingConfig();
          if (data && assetMappingConfig.AssetMappingConfigurationId && data.assetMappingConfigurationId === assetMappingConfig.AssetMappingConfigurationId) {
            this.updateProjectAssetMapping();
          }
        });
      });

    this.projectAssetMappingService().getModulesToCancelChanges()
      .subscribe((event) => {
        event.map((data) => {
          const assetMappingConfig = this.assetMappingConfig();
          if (data && assetMappingConfig.AssetMappingConfigurationId && data.assetMappingConfigurationId === assetMappingConfig.AssetMappingConfigurationId) {
            this.cancelChanges();
          }
        });
      });

    this.projectAssetMappingCostCenterService().changePending.subscribe(change => {
      let allocationPercentage = this.totalAllocation.getValue();
      this.projectAssetMappingService().setModuleAllocation(
        {
          assetMappingConfigurationId: this.assetMappingConfig().AssetMappingConfigurationId,
          moduleId: this.assetMappingConfig().ModuleId,
          allocationPercentage: allocationPercentage || 0,
          hasAssets: true,
          isActiveToggle: true,
          isToggle: this.assetMappingConfig().ModuleProperties.IsToggleModule,
          hasPendingChange: change
        }
      );
    });
  }

  private subscribeToProjectModuleEvents() {
    this.projectAssetMappingService().getModulesToReload()
      .subscribe((event) => {
        event.map((data) => {
          const assetMappingConfig = this.assetMappingConfig();
          if (data && assetMappingConfig.AssetMappingConfigurationId && data.assetMappingConfigurationId === assetMappingConfig.AssetMappingConfigurationId) {
            this.clearAllocation(data);
          }
        });
      });
  }

  private clearAllocation(module: IModuleAllocation) {
    this.loadAvailableCostCenters();
    this.loadMappedCostCenters();
    this.showToggleAllocation = true;
    this.projectAssetMappingService().setModuleAllocation(module);
  }

  private setToggleModule(hasAsset: boolean) {
    if (this.assetMappingConfig().ModuleProperties.IsToggleModule) {
      this.showToggleAllocation = !hasAsset;
    }
  }

  private updateProjectAssetMapping() {
    this.projectAssetMappingCostCenterService().saveChanges$().subscribe(() => {
      this.loadAvailableCostCenters();
      this.hasPendingChange = false;
    });
  }

  private cancelChanges() {
    this.projectAssetMappingCostCenterService().cancelChanges();
    this.loadAvailableCostCenters();
  }

  public toggleAllocationModule() {
    this.showToggleAllocation = false;
    this.projectAssetMappingService().toggleModuleAllocation(this.projectId(), this.assetMappingConfig().AssetMappingConfigurationId);
  }

  public costCenterDataStateChange(state: DataStateChangeEvent) {
    this.costCenterGridState = state;
    this.projectAssetMappingCostCenterService().read(this.costCenterGridState, this.formatQueryString());
  }

  public availableCostCenterPageChange(state: DataStateChangeEvent) {
    this.availableCostCentersGridState = state;
    let convertedGridState = this.convertStateForContainerIdContainsFilter();
    let gridState: State;

    gridState = convertedGridState ? convertedGridState : state;

    this.costCenterAvailableService.read(gridState, this.formatSelectionQueryString());
  }

  public createProjectAssetMappingFormGroup(args: any): any {
    const item = args.isNew ? new ProjectAssetMapping() : args.dataItem;

    this.projectAssetMappingFormGroup = this.formBuilder.group({
      'ProjectAssetMappingId': [item.ProjectAssetMappingId || 0],
      'ProjectId': [this.projectId()],
      'CostCenterId': [item.CostCenterId],
      'PercentAllocation': [item.PercentAllocation],
      'IsConstructedAsset': [false],
      'CostCenter': [null]
    });

    return this.projectAssetMappingFormGroup;
  }

  public createSelectionFormGroup(): any {
    return this.selectionFormGroup;
  }

  public setValuesFromCostCenters() {
    if (this.costCentersView) {
      this.costCenterMap = new Map();
      this.costCentersView
        .subscribe(x => {
          if (x) {
            let currentTotalCostCenters = this.totalCostCenters;
            let currentTotalAllocation = this.totalAllocation.getValue();
            let allocation = 0;
            x.data.map(a => {
              let costCenter = this.costCenterMap[a.CostCenterId];
              if (!costCenter && a.ProjectAssetMappingId > 0) {
                this.costCenterMap[a.CostCenterId] = a.CostCenter;
              }
              allocation = allocation + a.PercentAllocation;
            });

            this.totalCostCenters = x.total;

            if (currentTotalAllocation !== allocation || currentTotalCostCenters !== this.totalCostCenters) {
              if (this.initialLoad === false)
                this.hasPendingChange = true;
            }

            this.initialLoad = false;
            this.totalAllocation.next(allocation);
          }
        });
    }
  }

  public splitAllocation() {
    let splitPercentages = calculateSplitPercentages(1, this.totalCostCenters, 6);
    let holdingData: ProjectAssetMapping[];
    let allocation = 0;
    this.costCentersView.subscribe(a => holdingData = a.data);
    holdingData.map((x, ix) => {
      x.PercentAllocation = splitPercentages[ix];
      delete x.CostCenter;
      allocation = allocation + x.PercentAllocation;
      this.projectAssetMappingCostCenterService().update(x);
    });

    this.hasPendingChange = true;
    holdingData = null;
    this.totalAllocation.next(allocation);
  }

  private setValuesFromConfig() {
    let config = this.assetMappingConfig();

    if (config) {
      this.allocationAllowed = config.IsAllowAllocation && config.IsAllowAllocation;
      this.moduleIsEditable = config.ModuleProperties.IsMappingEditable && config.ModuleProperties.IsMappingEditable;
    }
  }

  public addCostCenterToMapping() {
    if (this.selectedCostCenters) {
      this.selectedCostCenters.forEach(item => {
        let newCostCenterMap = new NewCostCenterMap(item);
        let newMapping = new ProjectAssetMapping();
        newMapping.ProjectAssetMappingId = 0;
        newMapping.ProjectId = this.projectId();
        newMapping.CostCenterId = item.CostCenterId;
        newMapping.IsConstructedAsset = false;
        newMapping.PercentAllocation = 0;

        this.costCenterMap[item.CostCenterId] = newCostCenterMap.CostCenter;
        this.projectAssetMappingCostCenterService().create(newMapping);

        let selectionGridData = this.selectionGrid().data as any;
        let idx = selectionGridData.data.findIndex((x) => x.CostCenterId === item.CostCenterId);
        if (idx > -1) selectionGridData.data.splice(idx, 1);
      });

      this.selectedCostCenters = [];
    }
  }

  public costCenterSelectionKey(context: RowArgs): number {
    return context && context.dataItem;
  }

  public cancelMappingSelection() {
    this.selectedCostCenters = [];
  }

  public toggleSelection() {
    this.selectionInProgress = !this.selectionInProgress;
  }

  public editAllocationClose({ dataItem, rowIndex }: any) {
    let currentValue = 0;
    currentValue = this.projectAssetMappingFormGroup && this.projectAssetMappingFormGroup.value.PercentAllocation;
    let delta = currentValue - (dataItem.PercentAllocation);
    let allocation = this.totalAllocation.getValue();
    this.totalAllocation.next(allocation + delta);
  }
}

class NewCostCenterMap {
  CostCenterId: number;
  CostCenter: CostCenter;

  constructor(item?: any) {
    this.CostCenterId = item.CostCenterId;

    let newCostCenter = new CostCenter();
    newCostCenter.CostCenterNumber = item.CostCenterNumber;
    newCostCenter.Description = item.Description;

    let costCenterType = new CostCenterType();
    costCenterType.CostCenterTypeId = item.CostCenterTypeId;
    costCenterType.Description = item.CostCenterType;
    newCostCenter.CostCenterType = costCenterType;

    this.CostCenter = newCostCenter;
  }
}